import React, { useCallback, useRef, useState } from "react";
import { VariableSizeList as List } from "react-window";
import { Maybe } from "graphql/jsutils/Maybe";

interface VariableSizeListProps {
	data: Array<Maybe<any>>;
	height: number;
	RowComponent: React.FC<any>;
	onUpdate: (index: number, updatedQuest: any) => void;
	initialValue: number;
	loadMoreValue: number;
}

const defaultItemSize = 40;

const VariableSizeListComponent: React.FC<VariableSizeListProps> = ({
	data,
	height,
	RowComponent,
	onUpdate,
	initialValue,
	loadMoreValue,
}) => {
	const [itemsToShow, setItemsToShow] = useState(initialValue);
	const itemCount = Math.min(itemsToShow, data.length);
	const listRef = useRef<List>(null);
	const sizeMap = useRef<{ [index: number]: number }>({});

	const setSize = (index: number, size: number) => {
		if (sizeMap.current[index] !== size) {
			sizeMap.current = { ...sizeMap.current, [index]: size };
			listRef.current?.resetAfterIndex(index);
		}
	};

	const getSize = (index: number) => sizeMap.current[index] || defaultItemSize;

	const onItemsRendered = useCallback(
		({ visibleStopIndex }) => {
			if (visibleStopIndex >= itemCount - 1) {
				setItemsToShow((prev) => Math.min(prev + loadMoreValue, data.length));
			}
		},
		[itemCount, data.length, loadMoreValue]
	);

	return (
		<List
			height={height}
			itemCount={itemCount}
			itemSize={getSize}
			width="100%"
			onItemsRendered={onItemsRendered}
			ref={listRef}
			itemData={data}
		>
			{({ index, style, data }) => (
				<div style={style}>
					<RowComponent key={index} {...data[index]!} index={index} setItemSize={setSize} onUpdate={onUpdate} />
				</div>
			)}
		</List>
	);
};

export default VariableSizeListComponent;
